/**
 * Pooled one-shot playback.
 *
 * WebAudio source nodes are single-use by specification — an AudioBufferSourceNode cannot be
 * restarted — so "create every node once" cannot be taken literally for one-shots. What we
 * do instead, and what the phrase actually means in practice:
 *
 *   · Each of the N voice slots owns a PERSISTENT gain → stereo-panner chain, built once.
 *   · Triggering a sound creates exactly ONE node (the buffer source) and connects it to
 *     that slot. No filters, no envelopes, no panners are ever allocated at trigger time,
 *     because all of that is already baked into the buffer (see banks.js).
 *   · Polyphony is hard-capped. Past the cap the quietest, oldest voice is stolen.
 *   · Slots are reaped by comparing the audio clock against a stored end time — no `onended`
 *     callbacks, so there is no per-trigger closure and nothing for the GC to collect.
 *
 * Positional sounds use a cheap distance/pan model rather than a PannerNode: arcade sfx read
 * better without HRTF or doppler, and a PannerNode per voice would cost more than the sound.
 */

import { AUDIO_TUNING as T } from './tuning.js';

const X = T.sfx;

export class VoicePool {
  constructor(graph, randTable) {
    this.graph = graph;
    this.rand = randTable;
    this.built = false;
    this.slots = [];
    this.enabled = true;

    /** key → last trigger time (audio clock, or model clock when silent). */
    this._lastTrigger = new Map();
    this.playCount = 0;
    this.stolenCount = 0;
    this.suppressedCount = 0;
    this.missingCount = 0;
    /** Model clock so retrigger limiting behaves identically without an audio context. */
    this.clock = 0;

    // Listener frame, written by the owner each presentation step. No allocation per call.
    this.listener = { x: 0, y: 0, z: 0, rx: 1, rz: 0 };
  }

  build() {
    const g = this.graph;
    if (!g.live || this.built) return;
    const a = g.actx;
    for (let i = 0; i < X.maxVoices; i++) {
      const gain = a.createGain();
      gain.gain.value = 1;
      let pan = null;
      if (a.createStereoPanner) {
        pan = a.createStereoPanner();
        gain.connect(pan);
        pan.connect(g.bus.sfx);
      } else {
        gain.connect(g.bus.sfx);
      }
      this.slots.push({ gain, pan, src: null, endsAt: 0, gainValue: 0, key: null });
    }
    this.built = true;
  }

  /** Set the listener pose (camera). Called once per presentation frame. */
  setListener(px, py, pz, fx, fz) {
    const l = this.listener;
    l.x = px; l.y = py; l.z = pz;
    // Right vector of a Y-up camera looking along (fx, fz): perpendicular in the XZ plane.
    const len = Math.sqrt(fx * fx + fz * fz) || 1;
    l.rx = fz / len; l.rz = -fx / len;
  }

  _now() { return this.graph.live ? this.graph.actx.currentTime : this.clock; }

  /**
   * @param {string} key   an entry in AUDIO_TUNING.sfx.keys
   * @param {object} opts  {pos:{x,y,z}, gain, rate, variation}
   */
  play(key, opts) {
    const def = X.keys[key];
    if (!def) { this.missingCount++; return false; }

    const now = this._now();
    const last = this._lastTrigger.get(key);
    if (last !== undefined && (now - last) * 1000 < X.minRetriggerMs) { this.suppressedCount++; return false; }
    this._lastTrigger.set(key, now);
    this.playCount++;

    if (!this.graph.live || !this.built || !this.enabled) return true;

    let gain = def.gain * (opts && opts.gain != null ? opts.gain : 1);
    let rate = def.rate * (opts && opts.rate != null ? opts.rate : 1);
    const jitter = def.jitter * (opts && opts.variation != null ? opts.variation : 1);
    if (jitter > 0) rate *= 1 + this.rand.bi() * jitter;
    if (rate < 0.05) rate = 0.05;
    if (rate > 4) rate = 4;

    let pan = 0;
    if (opts && opts.pos) {
      const l = this.listener;
      const dx = opts.pos.x - l.x, dy = (opts.pos.y || 0) - l.y, dz = opts.pos.z - l.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d > X.maxDistance) return false;
      gain *= 1 / (1 + d / X.refDistance);
      if (d > 0.001) pan = Math.max(-1, Math.min(1, ((dx * l.rx + dz * l.rz) / d) * X.panWidth));
    }
    if (gain < 0.0015) return false;

    const buf = this.graph.buffer(def.buf);
    if (!buf) { this.missingCount++; return false; }

    const slot = this._acquire(gain);
    if (!slot) return false;

    const a = this.graph.actx;
    const src = a.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    src.connect(slot.gain);

    slot.gain.gain.setValueAtTime(gain, now);
    if (slot.pan) slot.pan.pan.setValueAtTime(pan, now);
    slot.gainValue = gain;
    slot.key = key;
    slot.endsAt = now + buf.duration / rate + 0.02;
    slot.src = src;
    src.start(now);
    return true;
  }

  /** Find a free slot, or steal the quietest one that has been running longest. */
  _acquire(wantGain) {
    const now = this._now();
    let best = null, bestScore = Infinity;
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i];
      if (s.endsAt <= now) {
        if (s.src) { try { s.src.disconnect(); } catch { /* already gone */ } s.src = null; }
        return s;
      }
      // Steal score: quiet and nearly finished is the cheapest to lose.
      const remaining = s.endsAt - now;
      const score = s.gainValue * 4 + remaining;
      if (score < bestScore) { bestScore = score; best = s; }
    }
    if (!best) return null;
    if (best.gainValue > wantGain * 2.2) return null;    // do not steal something much louder
    this.stolenCount++;
    if (best.src) {
      try { best.src.stop(now); } catch { /* already stopped */ }
      try { best.src.disconnect(); } catch { /* already gone */ }
      best.src = null;
    }
    return best;
  }

  /** Reap finished slots. Cheap: one pass over a fixed-size array, no allocation. */
  update(dt) {
    if (!this.graph.live) this.clock += dt;
    if (!this.built) return;
    const now = this._now();
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i];
      if (s.src && s.endsAt <= now) {
        try { s.src.disconnect(); } catch { /* already gone */ }
        s.src = null; s.gainValue = 0; s.key = null;
      }
    }
  }

  active() {
    let n = 0;
    const now = this._now();
    for (let i = 0; i < this.slots.length; i++) if (this.slots[i].endsAt > now && this.slots[i].src) n++;
    return n;
  }

  stopAll() {
    if (!this.built) return;
    const now = this._now();
    for (const s of this.slots) {
      if (s.src) { try { s.src.stop(now); } catch { /* ignore */ } try { s.src.disconnect(); } catch { /* ignore */ } }
      s.src = null; s.endsAt = 0; s.gainValue = 0; s.key = null;
    }
  }

  setEnabled(on) { this.enabled = on; if (!on) this.stopAll(); }

  reset() {
    this.stopAll();
    this._lastTrigger.clear();
    this.playCount = 0;
    this.stolenCount = 0;
    this.suppressedCount = 0;
    this.missingCount = 0;
    this.clock = 0;
  }

  stats() {
    return {
      played: this.playCount,
      active: this.active(),
      stolen: this.stolenCount,
      suppressed: this.suppressedCount,
      missing: this.missingCount,
      cap: X.maxVoices,
    };
  }
}
